/**
 * Live trivia results page — `/live/{id}/results`.
 *
 * Renders the post-game leaderboard. The server returns the top 50 for
 * regular players and the full list for admins (capped at 1000); the page
 * displays whatever it gets and adds a small "viewing as admin" badge for
 * the admin variant.
 */

import { api, ApiError } from '../api.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { shareWin } from '../lib/share_win.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

export async function renderLiveResults(root) {
  // Path: /live/{id}/results
  const parts = window.location.pathname.split('/').filter(Boolean);
  const liveId = parts[1];

  root.innerHTML = `
    <div class="appbar">
      <a href="/live" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Results</div>
    </div>
    <div class="pad" id="results-body">
      <div class="card muted">Loading results…</div>
    </div>
  `;

  // Fetch + audit-ready gate. The server sets results_ready=false in
  // the brief window between the trivia flipping to FINISHED and the
  // suspicion+settle audit landing. During that window we render an
  // interstitial and re-poll every 2s until the flag flips — so users
  // never see raw un-audited scores that could shuffle out from under
  // them. Once results_ready is true we fall through to the normal
  // leaderboard render.
  let data;
  const cancel = { yes: false };
  // If the user navigates away mid-poll, stop firing background fetches.
  const onPop = () => { cancel.yes = true; };
  window.addEventListener('popstate', onPop, { once: true });

  while (true) {
    try {
      data = await api(`/live/${liveId}/results`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        navigate('/login');
        return;
      }
      toast.fromError(err);
      document.getElementById('results-body').innerHTML =
        `<div class="card" style="color:var(--pink-t)">${escape(err.message)}</div>`;
      return;
    }
    if (cancel.yes) return;
    // Only kick the interstitial when the trivia is actually finished
    // — a still-live trivia's leaderboard is always "ready" to render
    // as intermediate standings.
    const stillAuditing = data.status === 'finished' && data.results_ready === false;
    if (!stillAuditing) break;
    _renderAuditInterstitial();
    await new Promise((resolve) => setTimeout(resolve, 2000));
    if (cancel.yes) return;
  }

  const body = document.getElementById('results-body');
  // Clear the interstitial state marker so subsequent renders don't
  // short-circuit the animation cleanup.
  if (body) delete body.dataset.state;
  const wonKobo = Number(data.your_payout_kobo || 0);
  const wonCashKobo = Number(data.your_cash_kobo || 0);
  const wonBonusKobo = Number(data.your_bonus_kobo || 0);
  // Show the cash/bonus split underneath the hero amount only when the
  // trivia had a non-zero bonus split — i.e. when bonus actually landed.
  // Otherwise the legacy "credited to your wallet instantly" copy is
  // strictly correct.
  const heroBreakdown = wonBonusKobo > 0
    ? `<div style="font-size:12px;font-weight:600;opacity:0.92;margin-top:6px;">
         ${escape(formatKobo(wonCashKobo))} cash · <span style="color:#FFD3EB;">${escape(formatKobo(wonBonusKobo))} bonus</span>
       </div>
       <div class="muted" style="font-size:10.5px;line-height:1.4;margin-top:4px;opacity:0.85;">
         Bonus spends first on duels &amp; power-ups. Not withdrawable.
       </div>`
    : '';
  body.innerHTML = `
    ${wonKobo > 0
      ? `<div class="hero violet center" style="padding:28px 16px;">
           <div style="font-family:var(--font-display);font-size:13px;letter-spacing:6px;font-weight:700;opacity:0.92;">
             YOU WON
           </div>
           <div style="font-family:var(--font-display);font-size:46px;color:#fff;font-weight:800;margin:10px 0;letter-spacing:-0.5px;">
             + ${escape(formatKobo(wonKobo))}
           </div>
           <div style="font-size:13px;font-weight:500;opacity:0.92;">
             credited to your wallet instantly
           </div>
           ${heroBreakdown}
           <button type="button" id="lr-share-btn"
                   style="margin-top:14px;padding:9px 22px;border-radius:999px;background:rgba(255,255,255,0.18);border:1px solid rgba(255,255,255,0.45);color:#fff;font-family:var(--font-display);font-size:13px;font-weight:700;letter-spacing:0.06em;cursor:pointer;">
             📣 Share win
           </button>
         </div>`
      : `<div class="hero gold center" style="padding:24px 16px;">
           <div style="font-family:var(--font-display);font-size:13px;letter-spacing:5px;opacity:0.85;">
             ${data.status === 'finished' ? 'FINAL RESULTS' : 'CURRENT STANDINGS'}
           </div>
           <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:700;margin-top:10px;">
             ${escape(data.title)}
           </div>
           <div class="muted" style="font-size:12px;margin-top:6px;">
             ${data.participants_count} ${data.participants_count === 1 ? 'player' : 'players'} ·
             ${data.question_count} questions${data.finished_at ? ` · ended ${escape(fmtTime(data.finished_at))}` : ''}
           </div>
         </div>`}

    ${data.your_rank
      ? `<div class="card" style="padding:14px;">
           <div class="row">
             <div class="grow">
               <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">Your finish</div>
               <div style="font-family:var(--font-display);font-size:20px;color:#fff;font-weight:700;margin-top:2px;">
                 #${data.your_rank} · ${data.your_total_points} pts
               </div>
             </div>
           </div>
         </div>`
      : `<div class="card muted" style="padding:14px;">
           You didn't appear on the leaderboard for this round.
         </div>`}

    ${(data.your_erasers_awarded || 0) > 0
      ? `<div class="card" style="padding:14px;margin-top:10px;background:rgba(229,193,0,0.10);border:1px solid rgba(229,193,0,0.45);">
           <div class="row" style="align-items:center;gap:12px;">
             <div style="font-size:30px;line-height:1;">🎉</div>
             <div class="grow">
               <div style="font-family:var(--font-display);font-size:15px;color:#fff;font-weight:700;">
                 You won ${data.your_erasers_awarded} eraser${data.your_erasers_awarded === 1 ? '' : 's'}!
               </div>
               <div class="muted" style="font-size:12px;line-height:1.4;margin-top:2px;">
                 Rank #${data.your_rank} landed inside the reward range. Already credited to your inventory.
               </div>
             </div>
             <a href="/shop" data-link class="tag" style="font-family:var(--font-display);text-decoration:none;background:rgba(229,193,0,0.15);color:#E5C100;font-weight:700;">
               ✏️ View
             </a>
           </div>
         </div>`
      : ''}

    ${(data.your_fiftyfifty_awarded || 0) > 0
      ? `<div class="card" style="padding:14px;margin-top:10px;background:rgba(233,75,162,0.08);border:1px solid rgba(233,75,162,0.45);">
           <div class="row" style="align-items:center;gap:12px;">
             <div style="font-size:30px;line-height:1;">🎯</div>
             <div class="grow">
               <div style="font-family:var(--font-display);font-size:15px;color:#fff;font-weight:700;">
                 You won ${data.your_fiftyfifty_awarded} 50/50${data.your_fiftyfifty_awarded === 1 ? '' : 's'}!
               </div>
               <div class="muted" style="font-size:12px;line-height:1.4;margin-top:2px;">
                 Rank #${data.your_rank} landed inside the 50/50 reward range. Use them on a future live trivia.
               </div>
             </div>
           </div>
         </div>`
      : ''}

    <div class="card" style="padding:12px;">
      <div class="row" style="margin-bottom:8px;">
        <div class="muted" style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;">
          ${data.is_admin_view ? 'All contestants (admin view)' : 'Top 50'}
        </div>
        ${data.is_admin_view
          ? `<div class="sp"></div><span class="tag bad" style="font-size:10px;">ADMIN</span>`
          : ''}
      </div>
      ${(data.leaderboard || []).length === 0
        ? `<div class="muted">No scores recorded.</div>`
        : (data.leaderboard || []).map((r, i) => `
            <div class="row" style="padding:7px 0;border-top:${i === 0 ? 'none' : '1px solid #2A2244'};">
              <span style="font-family:var(--font-display);font-size:13px;width:32px;color:${i < 3 ? '#FFE9A6' : 'var(--mute)'};">
                #${i + 1}
              </span>
              <span style="font-size:13px;color:#fff;flex:1;display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                ${escape(r.username || (r.user_id || '').slice(0, 8))}
                ${(r.erasers_used || 0) > 0
                  ? `<span title="Used ${r.erasers_used} eraser${r.erasers_used === 1 ? '' : 's'} during this game" style="display:inline-flex;align-items:center;gap:2px;padding:2px 6px;background:#1A1432;border:1px solid #2A2244;border-radius:999px;font-size:10px;color:#E5C100;font-weight:600;">
                       ✏️ Used${r.erasers_used > 1 ? ` ×${r.erasers_used}` : ''}
                     </span>`
                  : ''}
                ${(r.erasers_won || 0) > 0
                  ? `<span title="Won ${r.erasers_won} eraser${r.erasers_won === 1 ? '' : 's'} for finishing inside the reward range" style="display:inline-flex;align-items:center;gap:2px;padding:2px 6px;background:#3a2a14;border:1px solid rgba(229,193,0,0.45);border-radius:999px;font-size:10px;color:#E5C100;font-weight:700;">
                       🎉 Won ×${r.erasers_won}
                     </span>`
                  : ''}
                ${(r.fiftyfifty_used || 0) > 0
                  ? `<span title="Used 50/50 ${r.fiftyfifty_used} time${r.fiftyfifty_used === 1 ? '' : 's'} during this game" style="display:inline-flex;align-items:center;gap:2px;padding:2px 6px;background:#1A1432;border:1px solid #2A2244;border-radius:999px;font-size:10px;color:#E94BA2;font-weight:600;">
                       🎯 50/50${r.fiftyfifty_used > 1 ? ` ×${r.fiftyfifty_used}` : ''}
                     </span>`
                  : ''}
                ${(r.fiftyfifty_won || 0) > 0
                  ? `<span title="Won ${r.fiftyfifty_won} 50/50${r.fiftyfifty_won === 1 ? '' : 's'} for finishing inside the reward range" style="display:inline-flex;align-items:center;gap:2px;padding:2px 6px;background:#2c1428;border:1px solid rgba(233,75,162,0.55);border-radius:999px;font-size:10px;color:#E94BA2;font-weight:700;">
                       🎯 Won ×${r.fiftyfifty_won}
                     </span>`
                  : ''}
              </span>
              <span style="display:inline-flex;flex-direction:column;align-items:flex-end;width:64px;line-height:1.15;">
                <span style="font-family:var(--font-display);font-size:13px;color:#FFE9A6;">
                  ${r.points}
                </span>
                ${(r.answered || 0) > 0
                  ? `<span class="muted" title="Average time to answer per question" style="font-size:9px;letter-spacing:0.02em;margin-top:1px;">
                       ⚡ ${(r.total_ms / r.answered / 1000).toFixed(1)}s
                     </span>`
                  : ''}
              </span>
              ${(r.payout_kobo || 0) > 0
                ? `<span title="${(r.bonus_kobo || 0) > 0 ? `${escape(formatKobo(r.cash_kobo || 0))} cash · ${escape(formatKobo(r.bonus_kobo || 0))} bonus` : ''}" style="display:inline-flex;flex-direction:column;align-items:flex-end;padding:3px 8px;background:#3a2a14;border:1px solid rgba(229,193,0,0.45);border-radius:6px;font-family:var(--font-display);font-size:11px;color:#E5C100;font-weight:700;margin-left:6px;white-space:nowrap;line-height:1.15;">
                     <span>+${escape(formatKobo(r.payout_kobo))}</span>
                     ${(r.bonus_kobo || 0) > 0
                       ? `<span style="font-size:9px;font-weight:600;color:#E94BA2;letter-spacing:0.04em;margin-top:1px;">🎁 ${escape(formatKobo(r.bonus_kobo))} bonus</span>`
                       : ''}
                   </span>`
                : `<span class="muted" style="font-size:10px;width:46px;text-align:right;">
                     ${r.correct}/${data.question_count}
                   </span>`}
            </div>
          `).join('')}
    </div>

    <a href="/live" data-link class="btn ghost">Back to live trivia</a>
  `;

  // Share-win sheet: only mounted when the user actually won money.
  // The button itself is only rendered above when wonKobo > 0, so
  // querySelector returning null on a non-winning result is the
  // expected path — guard accordingly.
  const shareBtn = document.getElementById('lr-share-btn');
  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      shareBtn.disabled = true;
      const original = shareBtn.textContent;
      shareBtn.textContent = 'Preparing…';
      try {
        await shareWin({
          kind: 'live',
          amountKobo: wonKobo,
          title: data.title,
          subtitle: data.your_rank ? `Finished #${data.your_rank}` : undefined,
        });
      } finally {
        shareBtn.disabled = false;
        shareBtn.textContent = original;
      }
    });
  }
}

function _renderAuditInterstitial() {
  // Idempotent: only writes if the body isn't already showing the
  // interstitial. Prevents the pulse animation from restarting every
  // 2s poll cycle, which would flash the whole card.
  const body = document.getElementById('results-body');
  if (!body || body.dataset.state === 'auditing') return;
  body.dataset.state = 'auditing';
  body.innerHTML = `
    <div class="hero violet center" style="padding:36px 20px;">
      <div style="font-size:44px;line-height:1;">🛡️</div>
      <div style="font-family:var(--font-display);font-size:14px;letter-spacing:5px;font-weight:700;opacity:0.92;margin-top:14px;">
        AUDITING
      </div>
      <div style="font-family:var(--font-display);font-size:22px;color:#fff;font-weight:700;margin-top:8px;line-height:1.3;">
        Calculating final results…
      </div>
      <div class="muted" style="font-size:12.5px;line-height:1.5;margin-top:10px;opacity:0.9;">
        We're checking every top-ranked player for fair play before
        prizes are settled. This usually takes a few seconds.
      </div>
      <div style="margin-top:20px;display:flex;justify-content:center;gap:6px;">
        <span class="audit-dot" style="width:8px;height:8px;border-radius:50%;background:#fff;opacity:0.4;animation:audit-pulse 1.4s ease-in-out infinite;"></span>
        <span class="audit-dot" style="width:8px;height:8px;border-radius:50%;background:#fff;opacity:0.4;animation:audit-pulse 1.4s ease-in-out infinite;animation-delay:0.2s;"></span>
        <span class="audit-dot" style="width:8px;height:8px;border-radius:50%;background:#fff;opacity:0.4;animation:audit-pulse 1.4s ease-in-out infinite;animation-delay:0.4s;"></span>
      </div>
    </div>
    <style>
      @keyframes audit-pulse {
        0%, 100% { opacity: 0.35; transform: scale(0.85); }
        50%      { opacity: 1;    transform: scale(1);    }
      }
    </style>
    <a href="/policy/anti-cheat" data-link
       style="display:flex;align-items:center;justify-content:center;gap:6px;margin-top:14px;padding:10px;text-decoration:none;color:var(--mute);font-size:11.5px;line-height:1.4;">
      <span>Why we audit results</span>
      <span style="color:#5EE0C6;text-decoration:underline;">Know more →</span>
    </a>
  `;
}
